import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { ADMIN_ROLE_LABELS } from "@/lib/roles";
import { EnvironmentSwitcher } from "@/components/environment/EnvironmentSwitcher";
import { enabledDomains } from "@/router/domain-flags";

interface ModuleCard {
  key: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  borderHover: string;
  path: string;
  status: string;
  statusColor: string;
  stats: { label: string; value: string }[];
}

const moduleDomainEnabled: Record<ModuleCard["key"], boolean> = {
  crypto: enabledDomains.crypto,
  us_stock: enabledDomains.usStock,
  forex: enabledDomains.forex,
  ai_image: enabledDomains.ai,
  tianji: enabledDomains.tianji,
};

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { admin, canAccess, logout, setModule, currentEnv } = useAdminAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const modules: ModuleCard[] = [
    {
      key: "crypto",
      title: "币圈量化",
      subtitle: "Crypto Trading",
      description: "数字货币策略、模型与决策风格配置，查询用户、Trade Agent、AI 决策及交易事实",
      icon: "ri-coins-line",
      iconBg: "bg-brand/10",
      iconColor: "text-brand",
      borderHover: "hover:border-brand/40",
      path: "/crypto/overview",
      status: "运行中",
      statusColor: "bg-brand/10 text-brand",
      stats: [
        { label: "管理用户", value: "用户 / Agent" },
        { label: "配置管理", value: "策略 / 模型 / 风格" },
        { label: "决策审计", value: "决策 / 交易" },
      ],
    },
    {
      key: "us_stock",
      title: "美股量化",
      subtitle: "US Stock Trading",
      description: "美股市场量化策略管理，支持策略配置、仓位监控与收益分析",
      icon: "ri-bar-chart-line",
      iconBg: "bg-amber-500/10",
      iconColor: "text-amber-600",
      borderHover: "hover:border-amber-400/40",
      path: "/us-stock",
      status: "即将上线",
      statusColor: "bg-amber-500/10 text-amber-600",
      stats: [
        { label: "策略管理", value: "策略 / 回测" },
        { label: "仓位监控", value: "持仓 / 收益" },
        { label: "市场数据", value: "行情 / 分析" },
      ],
    },
    {
      key: "forex",
      title: "外汇量化",
      subtitle: "Forex Trading",
      description: "外汇市场交易管理，覆盖主要货币对与流动性监控",
      icon: "ri-exchange-dollar-line",
      iconBg: "bg-sky-500/10",
      iconColor: "text-sky-600",
      borderHover: "hover:border-sky-400/40",
      path: "/forex",
      status: "即将上线",
      statusColor: "bg-sky-500/10 text-sky-600",
      stats: [
        { label: "货币对", value: "主要 / 交叉" },
        { label: "流动性", value: "深度 / 监控" },
        { label: "交易执行", value: "下单 / 风控" },
      ],
    },
    {
      key: "ai_image",
      title: "AI 生图",
      subtitle: "NananoImage2",
      description: "AI 图像生成运营管理，涵盖用户管理、生成记录、VIP 套餐、内容安全审计等全链路功能",
      icon: "ri-image-ai-line",
      iconBg: "bg-rose-500/10",
      iconColor: "text-rose-500",
      borderHover: "hover:border-rose-400/40",
      path: "/ai/reports",
      status: "运行中",
      statusColor: "bg-rose-500/10 text-rose-500",
      stats: [
        { label: "用户管理", value: "用户 / VIP" },
        { label: "内容生成", value: "记录 / 审计" },
        { label: "运营配置", value: "套餐 / 积分" },
      ],
    },
    {
      key: "tianji",
      title: "天机",
      subtitle: "Tianji Operations",
      description: "天机用户、Apple 订阅支付、命理结果、AI 用量与上架审核账号管理",
      icon: "ri-compass-3-line",
      iconBg: "bg-violet-500/10",
      iconColor: "text-violet-500",
      borderHover: "hover:border-violet-400/40",
      path: "/tianji/overview",
      status: "运行中",
      statusColor: "bg-violet-500/10 text-violet-500",
      stats: [
        { label: "支付管理", value: "订单 / 流水" },
        { label: "运营统计", value: "命理 / AI" },
        { label: "审核支持", value: "白名单 / 权益" },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      {/* Top Bar */}
      <header className="h-[64px] md:h-[72px] bg-white border-b border-surface-border flex items-center justify-between px-4 md:px-8 shrink-0">
        <div className="flex items-center gap-2.5 md:gap-3">
          <div className="w-8 h-8 md:w-9 md:h-9 rounded-xl bg-brand flex items-center justify-center">
            <i className="ri-bar-chart-grouped-line text-white text-base md:text-lg" />
          </div>
          <span className="text-text-primary font-semibold text-sm md:text-base">钜睿科技</span>
          <span className="text-text-muted text-xs md:text-sm hidden sm:inline">管理后台</span>
        </div>
        <div className="flex items-center gap-3 md:gap-4">
          <div className="hidden sm:block">
            <EnvironmentSwitcher />
          </div>
          {/* User Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu((s) => !s)}
              className="flex items-center gap-2 pl-1 pr-1 md:pr-2 py-1.5 rounded-xl hover:bg-surface transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center">
                <i className="ri-user-line text-brand text-sm" />
              </div>
              <span className="text-text-secondary text-sm hidden sm:block max-w-[120px] md:max-w-[160px] truncate">
                {admin?.email || "管理员"}
              </span>
              <i className={`ri-arrow-down-s-line text-text-muted text-xs transition-transform hidden sm:block ${showUserMenu ? "rotate-180" : ""}`} />
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-[200px] md:w-[220px] bg-white rounded-2xl border border-surface-border py-2 z-50">
                  <div className="px-4 py-3 border-b border-surface-border">
                    <p className="text-text-primary text-sm font-medium truncate">
                      {admin?.email || "管理员"}
                    </p>
                    <p className="text-text-muted text-xs mt-0.5">
                      {admin?.role ? ADMIN_ROLE_LABELS[admin.role] ?? admin.role : ""}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-status-danger hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line text-base" />
                    <span>退出登录</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 md:px-6 py-8 md:py-12">
        {currentEnv?.is_prod && (
          <div className="w-full max-w-[1080px] mb-6 bg-status-danger/5 border border-status-danger/15 rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-status-danger/10 flex items-center justify-center shrink-0">
              <i className="ri-error-warning-line text-status-danger text-lg" />
            </div>
            <div>
              <div className="text-sm font-medium text-status-danger">
                当前处于 {currentEnv.display_name}，所有操作将影响真实业务
              </div>
              <div className="text-xs text-text-secondary mt-0.5">
                生产环境高危操作将触发二次确认，请谨慎操作
              </div>
            </div>
          </div>
        )}

        {/* Welcome */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-text-primary mb-2 md:mb-3">
            欢迎回来，{admin?.email?.split("@")[0] || "管理员"}
          </h1>
          <p className="text-text-secondary text-sm md:text-base lg:text-lg max-w-lg mx-auto px-2">
            请选择要进入的业务模块，开始今天的管理工作
          </p>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full max-w-[1080px]">
          {modules.filter((mod) => moduleDomainEnabled[mod.key]).map((mod) => {
            const hasAccess = canAccess(mod.key as "crypto" | "us_stock" | "forex" | "ai_image" | "tianji");
            const isRunning = mod.status === "运行中";

            return (
              <div
                key={mod.key}
                onClick={() => {
                  if (hasAccess) {
                    setModule(mod.key as "crypto" | "us_stock" | "forex" | "ai_image" | "tianji");
                    navigate(mod.path);
                  }
                }}
                className={`relative bg-white rounded-2xl border border-surface-border p-5 md:p-8 transition-all duration-300 ${
                  hasAccess
                    ? `${mod.borderHover} hover:shadow-lg cursor-pointer hover:-translate-y-1`
                    : "opacity-60 cursor-not-allowed"
                }`}
              >
                {/* Status Badge */}
                <div className="absolute top-4 right-4 md:top-5 md:right-5">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 md:px-3 py-1 rounded-full text-[11px] md:text-xs font-medium ${mod.statusColor}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isRunning ? "bg-brand" : "bg-amber-500"}`} />
                    {mod.status}
                  </span>
                </div>

                {/* Icon */}
                <div
                  className={`w-12 h-12 md:w-14 md:h-14 rounded-2xl ${mod.iconBg} flex items-center justify-center mb-5 md:mb-6`}
                >
                  <i className={`${mod.icon} ${mod.iconColor} text-xl md:text-2xl`} />
                </div>

                {/* Title */}
                <h3 className="text-lg md:text-xl font-semibold text-text-primary mb-1">
                  {mod.title}
                </h3>
                <p className="text-text-muted text-xs md:text-sm mb-3 md:mb-4">{mod.subtitle}</p>

                {/* Description */}
                <p className="text-text-secondary text-sm leading-relaxed mb-5 md:mb-6">
                  {mod.description}
                </p>

                {/* Stats */}
                <div className="space-y-2.5 md:space-y-3 mb-6 md:mb-8">
                  {mod.stats.map((stat, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-text-muted text-xs">{stat.label}</span>
                      <span className="text-text-secondary text-xs font-medium">
                        {stat.value}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <button
                  disabled={!hasAccess}
                  className={`w-full h-11 md:h-12 rounded-xl text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                    hasAccess
                      ? isRunning
                        ? "bg-brand text-white hover:bg-brand-dark shadow-lg shadow-brand/20"
                        : "bg-surface text-text-primary border border-surface-border hover:bg-surface/80"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {hasAccess ? (
                    <span className="flex items-center justify-center gap-2">
                      进入模块
                      <i className="ri-arrow-right-line" />
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-lock-line" />
                      无权限访问
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Global Admin Section - SUPER_ADMIN only */}
        {canAccess("admin_accounts") && (
          <div className="mt-8 md:mt-10 w-full max-w-[1080px] px-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-5 rounded-full bg-brand" />
              <h2 className="text-text-primary text-sm font-semibold tracking-wide">系统管理</h2>
              <span className="ml-1 px-2 py-0.5 rounded-full bg-brand/10 text-brand text-[11px] font-medium">
                超级管理员专属
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div
                onClick={() => navigate("/admin-users")}
                className="bg-white rounded-2xl border border-surface-border p-5 flex items-center gap-4 hover:border-brand/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                  <i className="ri-admin-line text-brand text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-text-primary text-sm font-semibold mb-0.5">账号管理</h3>
                  <p className="text-text-muted text-xs">管理员账号创建、角色分配与权限控制</p>
                </div>
                <i className="ri-arrow-right-line text-text-muted group-hover:text-brand transition-colors text-base shrink-0" />
              </div>
              <div
                onClick={() => navigate("/system-config")}
                className="bg-white rounded-2xl border border-surface-border p-5 flex items-center gap-4 hover:border-brand/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                  <i className="ri-settings-3-line text-brand text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-text-primary text-sm font-semibold mb-0.5">系统配置(JSON)</h3>
                  <p className="text-text-muted text-xs">全局系统参数与运营开关的 JSON 配置管理</p>
                </div>
                <i className="ri-arrow-right-line text-text-muted group-hover:text-brand transition-colors text-base shrink-0" />
              </div>
            </div>
          </div>
        )}

        {/* Quick Tips */}
        <div className="mt-8 md:mt-10 max-w-[720px] w-full px-1">
          <div className="bg-white rounded-2xl border border-surface-border p-4 md:p-6">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <i className="ri-lightbulb-line text-brand text-base md:text-lg" />
              </div>
              <div className="min-w-0">
                <h4 className="text-text-primary text-sm font-medium mb-1">
                  快速上手提示
                </h4>
                <ul className="text-text-secondary text-xs md:text-sm space-y-1.5">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-brand text-xs mt-0.5 shrink-0" />
                    <span>首次登录建议查看仪表盘了解平台整体运营状态</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-brand text-xs mt-0.5 shrink-0" />
                    <span>待审核提现和欠款账户会在仪表盘优先提醒</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-brand text-xs mt-0.5 shrink-0" />
                    <span>右上角通知铃铛可查看最新系统消息</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-surface-border py-4 md:py-5 px-4 md:px-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 md:gap-3 text-text-muted text-xs">
          <span>钜睿科技量化交易系统 v2.0</span>
          <span>
            {new Date().toLocaleDateString("zh-CN", {
              year: "numeric",
              month: "long",
              day: "numeric",
              weekday: "long",
            })}
          </span>
        </div>
      </footer>
    </div>
  );
}
